import { useEffect, useMemo, useRef } from 'react'
import { trackEvent } from 'analytics/analytics'
import { setOptimizelyExperiment } from 'actions/OptimizelyActions'
import { activateCampaignEvent } from 'analytics/snowplow/events'
import config from 'constants/config'
import type { OptimizelyExperiments } from 'constants/optimizely'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import {
  isAllowedToTriggerExperiment,
  setOptimizelyTriggeredCookie,
} from 'lib/optimizely/optimizelyUtils'

const optimizelyExperimentSet = new Set(config.OPTIMIZELY_EXPERIMENTS ?? [])

function useOptimizelyActivation(experimentId: OptimizelyExperiments, canTrigger: boolean = true, overrideTrigger = false) {
  const dispatch = useAppDispatch()
  const hasTriggeredEvent = useRef(false)
  const previousVariationKey = useRef<string | undefined>(undefined)
  const utmContent = useAppSelector(state => state.utm.content)

  const canTriggerExperiment = useMemo(() => {
    if (!optimizelyExperimentSet.has(experimentId)) {
      return false
    }
    if (overrideTrigger) {
      return canTrigger
    }
    return isAllowedToTriggerExperiment(canTrigger, utmContent, experimentId)
  }, [canTrigger, overrideTrigger, utmContent, experimentId])

  const experiment = useAppSelector(state =>
    state.optimizely.optimizelyExperiments[experimentId])

  const currentVariationKey = experiment?.variationKey
  const experimentTriggered = experiment?.triggered
  useEffect(() => {
    if (currentVariationKey !== previousVariationKey.current) {
      if (hasTriggeredEvent.current && experimentTriggered === false) {
        hasTriggeredEvent.current = false
      }
      previousVariationKey.current = currentVariationKey
    }
  }, [currentVariationKey, experimentTriggered])

  useEffect(() => {
    if (canTriggerExperiment && !hasTriggeredEvent.current && !experiment?.triggered &&
      experiment?.campaignId &&
      experiment?.experimentId) {
      const variationId = experiment.variation?.[experiment.variationKey]
      if (variationId) {
        trackEvent(activateCampaignEvent(
          experiment.campaignId,
          experiment.experimentId,
          variationId,
        ))

        dispatch(setOptimizelyExperiment(experimentId, experiment))
        setOptimizelyTriggeredCookie(experimentId)
        hasTriggeredEvent.current = true
      }
    }
  }, [dispatch, canTriggerExperiment, experimentId, experiment])

  return canTriggerExperiment
}

export default useOptimizelyActivation
